/**
 * Client-safe Sentry capture for `"use client"` components.
 *
 * The main capture helpers in ./capture.ts import server-only modules
 * (next/server `after`, @prisma/client, and ../db → Prisma) and therefore
 * CANNOT be imported into a `"use client"` file — doing so crashes the client
 * bundle (CLAUDE.md Critical Rule 5, client/server boundary). This wrapper
 * gives browser code the same "never call raw Sentry.captureException in a
 * component" guarantee: every capture is tagged errorSource:"client" and
 * carries a feature/errorType (plus optional HTTP status and structured
 * context), so failures group cleanly and stay observable.
 *
 * Only import `@sentry/nextjs` here — nothing server-only — so this file is
 * safe to import from any client component.
 *
 * Quiet paths — both produce level "info" with no userImpact tag:
 *   quietInfo: message-matched expected errors (session expiry, flag-off sentinels)
 *   expected:true: caller-declared expected outcomes (e.g. no-text deck extraction
 *     when the server vision path will handle it regardless — Sentry 7727687630)
 * A TRPCClientError with a 4xx-class `data.code` is quieted the same way later,
 * in beforeSend (sentry.config.ts), unless this call passed impact "blocking".
 * That is why `error` goes to Sentry unwrapped: beforeSend reads `data.code`
 * off `hint.originalException`.
 *
 * Network drops (isNetworkDropError) are captured at transient whatever the
 * caller passed, tagged networkDrop:"true". Every call site's catch receives
 * the browser's dropped connection, so per-site impact cannot tell a user's
 * wifi from our fault (LEV-1EZ: Safari "Load failed" paged as fatal). A spike
 * across users is the outage signal; the metric alert in
 * infra/sentry/alert-definitions.ts keys on that tag.
 */
import * as Sentry from "@sentry/nextjs";
import type { UserImpact } from "./capture";
import { isSessionExpiredMessage } from "@/lib/trpc/sessionExpiredMessage";
import { isTaskNotesV3NotEnabledMessage } from "@/lib/tasks/taskNotesV3NotEnabled";
import { isNetworkDropError } from "./networkDrop";

// Client-side mirror of IMPACT_LEVEL in ./capture — that module is
// server-only (pino, next/server), so only the TYPE crosses the boundary and
// the tiny level map is restated here. Keep the two in sync.
const CLIENT_IMPACT_LEVEL: Record<UserImpact, "fatal" | "error" | "warning"> = {
  blocking: "fatal",
  degraded: "error",
  transient: "warning",
};

export interface ClientErrorContext {
  /** Coarse feature bucket for grouping, e.g. "onboarding-chat". */
  feature: string;
  /** Specific failure type within the feature, e.g. "deck-vision-parse". */
  errorType: string;
  /** HTTP status when the failure is a fetch response; omitted otherwise. */
  httpStatus?: number | null;
  /**
   * User-impact classification (same contract as the server's captureError):
   * `blocking` → fatal (pages loudly), `degraded` → error, `transient` →
   * warning. Omitted → plain error, the pre-existing behavior.
   */
  impact?: UserImpact;
  /**
   * Extra non-indexed context grouped by section. MUST NOT contain PII, raw
   * user text, or AI output — counts, booleans, lengths, ids, and statuses only
   * (analytics-conventions.md applies to error context too).
   */
  contexts?: Record<string, Record<string, unknown>>;
  /**
   * Extra INDEXED tags (searchable in Sentry). Low-cardinality enum-like
   * values only — never ids, tokens, or free text. The reserved keys
   * (errorSource, feature, errorType, userImpact, httpStatus, networkDrop,
   * trpcExpected4xx) always win.
   */
  tags?: Record<string, string>;
  /**
   * Mark this failure as expected / non-actionable. Captures at level "info"
   * with no userImpact tag, regardless of `impact`. Use when the error is
   * observable but the operator / another code path already handles the case
   * correctly — e.g. a no-text deck when server vision runs regardless.
   * Distinct from the message-matched `quietInfo` path: this is an explicit
   * caller declaration rather than a message pattern match.
   */
  expected?: true;
}

/**
 * Capture a client-side error to Sentry with consistent, indexed tags. Use this
 * from `"use client"` components instead of calling `Sentry.captureException`
 * directly.
 */
export function captureClientError(
  error: unknown,
  context: ClientErrorContext,
): void {
  const { feature, errorType, httpStatus, contexts, expected } = context;
  const quietInfo =
    expected ||
    isSessionExpiredMessage(error) ||
    isTaskNotesV3NotEnabledMessage(error);
  const networkDrop = !quietInfo && isNetworkDropError(error);
  const impact: UserImpact | undefined = networkDrop ? "transient" : context.impact;
  // A caller tag must not claim networkDrop or trpcExpected4xx: each feeds a
  // spike alert, and only this function / beforeSend may decide them.
  const {
    networkDrop: _callerNetworkDrop,
    trpcExpected4xx: _callerTrpcExpected4xx,
    ...tags
  } = context.tags ?? {};
  Sentry.captureException(error, {
    ...(quietInfo
      ? { level: "info" as const }
      : impact
        ? { level: CLIENT_IMPACT_LEVEL[impact] }
        : {}),
    tags: {
      ...tags,
      errorSource: "client",
      feature,
      errorType,
      ...(!quietInfo && impact ? { userImpact: impact } : {}),
      ...(networkDrop ? { networkDrop: "true" } : {}),
      ...(httpStatus != null ? { httpStatus: String(httpStatus) } : {}),
    },
    ...(contexts ? { contexts } : {}),
  });
}
